// Live screen state: load from the API, then re-read it whenever the
// restaurant's event stream says something this screen shows has changed
// (docs/DINING_API_2026-10-28.md §13). The screen never patches money or
// states itself from an event - it asks the server again.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDiningEvents } from '@/lib/diningEvents';
import { authMerchantId, authToken } from './api';

export function useLive(loader, deps, { types, match, enabled = true, debounceMs = 180, onEvent } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const matchRef = useRef(match);
  matchRef.current = match;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const timer = useRef(null);
  const seq = useRef(0);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const next = await loaderRef.current();
      if (!alive.current || mine !== seq.current) return next;
      setData(next);
      setError(null);
      return next;
    } catch (e) {
      if (alive.current && mine === seq.current) setError(e);
      return null;
    } finally {
      if (alive.current && mine === seq.current) setLoading(false);
    }
  }, []);

  const soon = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(reload, debounceMs);
  }, [reload, debounceMs]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; clearTimeout(timer.current); };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => soon();
    const onVisible = () => { if (document.visibilityState === 'visible') soon(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, soon]);

  const { status } = useDiningEvents({
    enabled,
    getToken: authToken,
    merchantId: authMerchantId() || undefined,
    types,
    onEvent: (event) => {
      if (onEventRef.current) onEventRef.current(event);
      if (!matchRef.current || matchRef.current(event)) soon();
    },
    onReset: () => soon(),
  });

  return { data, error, loading, reload, refreshSoon: soon, streamStatus: status, setData };
}

export default useLive;
